import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Trash2,
  Edit,
  Tag,
} from 'lucide-react';
import CategoryModal from '../../components/CategoryModal';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import useApi from '../../../../hooks/useApi';
import {
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory
} from '../../../../services/finance_services/expenseService';

const ExpenseCategoryPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Use the useApi hook for different API operations
  const {
    data: categories,
    loading,
    error,
    callApi: fetchCategories,
    setError,
    setSuccessMessage
  } = useApi(getExpenseCategories, {showSuccessToast: false});

  const {
    loading: saveLoading,
    error: saveError,
    successMessage: saveSuccess,
    callApi: saveCategory,
    setError: setSaveError,
    setSuccessMessage: setSaveSuccess
  } = useApi(currentCategory?.id ? updateExpenseCategory : createExpenseCategory, {showSuccessToast: true, msg: currentCategory?.id ? 'Expense category updated': 'New expense category created'});

  const {
    loading: deleteLoading,
    error: deleteError,
    successMessage: deleteSuccess,
    callApi: removeCategory,
    setError: setDeleteError,
    setSuccessMessage: setDeleteSuccess
  } = useApi(deleteExpenseCategory, {showSuccessToast: true, msg: 'Deleted category'});

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Filter function
  const filteredCategories = categories?.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Handlers for modal and saving
  const handleAddCategory = () => {
    setCurrentCategory(null);
    setIsModalOpen(true);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    setIsModalOpen(true);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
    setDeleteError(null);
    setDeleteSuccess(null);
  };

  const confirmDeleteCategory = async () => {
    setIsDeleteModalOpen(false);
    try {
      await removeCategory(categoryToDelete.id);
      setDeleteSuccess('Category deleted successfully!');
      fetchCategories(); // Refetch the list
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete category.');
    } finally {
      setCategoryToDelete(null);
    }
  };

  const cancelDeleteCategory = () => {
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleSaveCategory = async (categoryData) => {
    setSaveError(null);
    setSaveSuccess(null);
    
    try {
      if (categoryData.id) {
        await saveCategory(categoryData.id, categoryData);
        setSaveSuccess('Category updated successfully!');
      } else {
        await saveCategory(categoryData);
        setSaveSuccess('Category added successfully!');
      }
      setIsModalOpen(false);
      fetchCategories(); // Refetch the list
    } catch (err) {
      setSaveError(err.message || 'Failed to save category.');
    }
  };

  // Combined error and success messages
  const displayError = error || saveError || deleteError;
  const displaySuccess = saveSuccess || deleteSuccess;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Categories</h1>
          <p className="text-gray-600">Organize your expenses by category</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleAddCategory}
            className="inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search categories..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-1/3"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Display Error Message */}
      {displayError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{displayError}</span>
        </div>
      )}

      {/* Display Success Message */}
      {displaySuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Success: </strong>
          <span className="block sm:inline">{displaySuccess}</span>
        </div>
      )}

      {/* Category Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  <th className="px-4 sm:px-6 py-3">Category Name</th>
                  <th className="px-4 sm:px-6 py-3 hidden sm:table-cell">Description</th>
                  <th className="px-4 sm:px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map(category => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="font-medium text-gray-900">{category.name}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-500 hidden sm:table-cell">
                        {category.description}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <Tag className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No categories found</h3>
                        <p className="text-gray-500 mb-4">Add your first expense category to get started.</p>
                        <button
                          onClick={handleAddCategory}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Category
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit Category */}
      {isModalOpen && (
        <CategoryModal
          category={currentCategory}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveCategory}
          loading={saveLoading}
        />
      )}

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeleteCategory}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${categoryToDelete?.name}"? This action cannot be undone.`}
        warning="Expenses under this category will have their category set to unassigned, not deleted."
      />
    </div>
  );
};

export default ExpenseCategoryPage;